import {
  Inject,
  Injectable,
  LOCALE_ID
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable
} from 'rxjs';
import { map } from 'rxjs/operators';
import {
  select,
  Store
} from '@ngrx/store';

import { environment } from '../../../environments/environment';
import { Watchlist } from '../../watchlists/model/watchlist/watchlist';
import { WatchlistBackendInterface } from '../../watchlists/model/watchlist/watchlist.backend-interface';
import { generateWatchlistFromBackendInput } from '../../watchlists/model/watchlist/watchlist.generators';
import { WatchlistForm } from '../../watchlists/model/watchlist/watchlist-form';
import { WatchlistElementForm } from '../../watchlists/model/watchlist-element/watchlist-element-form';
import { ElementWatchlistMapping } from '../../watchlists/model/element-watchlist-mapping/element-watchlist-mapping';
import { WatchlistElementBackend } from '../../watchlists/model/watchlist-element/watchlist-element.backend-interface';
import * as categoryTreeReducer from '../../category-tree/reducer';


/**
 * Service for my watchlists server interactions
 */
@Injectable()
export class MyWatchlistsService {

  private _treeUuid: string;

  constructor(private _httpClient: HttpClient, @Inject(LOCALE_ID) private _locale: string, private _store: Store<categoryTreeReducer.State>) {
    this._store.pipe(
      select(categoryTreeReducer.getCategoryTreeUuid)
    ).subscribe(treeUuid => this._treeUuid = treeUuid);
  }


  createMyWatchlist(watchlistData: WatchlistForm): Observable<Watchlist> {
    const url = this._buildUrl(`${ environment.api_url }watchlists`, true);
    return this._httpClient.post(url, watchlistData).pipe(
      map((body: {Watchlist: WatchlistBackendInterface; SubWatchlists: WatchlistBackendInterface[]}) =>
        body && body.Watchlist ? generateWatchlistFromBackendInput(body.Watchlist) : null
      )
    );
  }

  patchMyWatchlist(uuid: string, watchlistData: WatchlistForm): Observable<Watchlist> {
    const url = this._buildUrl(`${ environment.api_url }watchlists/${ uuid }`, true);
    return this._httpClient.patch(url, watchlistData).pipe(
      map((body: {Watchlist: WatchlistBackendInterface}) =>
        body && body.Watchlist ? generateWatchlistFromBackendInput(body.Watchlist) : null)
    );
  }

  getMyWatchlists(): Observable<{[uuid: string]: Watchlist}> {
    const url = this._buildUrl(`${ environment.api_url }watchlists/summary?locale=${ this._locale }`, false);
    return this._httpClient.get<WatchlistBackendInterface[]>(url).pipe(
      map(response => response ? this._createObjectFromFlatlist(response) : null)
    );
  }

  getMyWatchlistsFirstLevel(): Observable<{[uuid: string]: Watchlist}> {
    const url = this._buildUrl(`${ environment.api_url }watchlists?locale=${ this._locale }`, false);
    return this._httpClient.get<{SubWatchlists: WatchlistBackendInterface[]}>(url).pipe(
      map(response => Array.isArray(response.SubWatchlists) ? this._createObjectFromFlatlist(response.SubWatchlists) : null)
    );
  }

  fetchMyWatchlistDetail(uuid: string) {
    const url = this._buildUrl(`${ environment.api_url }watchlists/${ uuid }?locale=${ this._locale }`, false);
    return this._httpClient.get<{Watchlist: WatchlistBackendInterface; SubWatchlists: WatchlistBackendInterface[]}>
    (url).pipe(
      map(response => ({
        watchlist: response.Watchlist ? generateWatchlistFromBackendInput(response.Watchlist) : null,
        subWatchlists: Array.isArray(response.SubWatchlists) ? response.SubWatchlists.map(generateWatchlistFromBackendInput) : null
      }))
    );
  }

  removeMyWatchlist(uuid: string): Observable<void> {
    const url = this._buildUrl(`${ environment.api_url }watchlists/${ uuid }`, true);
    return this._httpClient.delete<void>(url);
  }

  addElementToMyWatchlist(watchlistUuid: string, element: WatchlistElementForm & {additionalProperties?: {productUuid?: string}}): Observable<void> {
    const url = this._buildUrl(`${ environment.api_url }watchlists/${ watchlistUuid }/items`, true);
    return this._httpClient.post<void>(url, element);
  }

  removeElementFromMyWatchlist(watchlistUuid: string, elementUuid: string): Observable<void> {
    const url = this._buildUrl(`${ environment.api_url }watchlists/${ watchlistUuid }/items/${ elementUuid }`, true);
    return this._httpClient.delete<void>(url);
  }

  commentMyWatchlistPreviewElement(watchlistUuid: string, elementUuid: string, watchlistComment: string): Observable<Record<string, unknown>> {
    const url = this._buildUrl(`${ environment.api_url }watchlists/${ watchlistUuid }/items/${ elementUuid }`, true);
    return this._httpClient.patch<{[key: string]: WatchlistElementBackend}>(
      url,
      {comment: watchlistComment});
  }

  /**
   * Creates a watchlist snapshot for the watchlist with the given uuid
   *
   * @param watchlistUuid
   * @returns
   */
  createWatchlistSnapshot(watchlistUuid: string): Observable<string> {
    const url = this._buildUrl(`${ environment.api_url }watchlists/${ watchlistUuid }/snapshot`, true);
    return this._httpClient.post<{watchlistSnapshot: string}>(url, {}).pipe(
      map(body => body && body.watchlistSnapshot ? body.watchlistSnapshot : undefined)
    );
  }

  /**
   * Fetches element to watchlist mapping
   *
   * @param elementUuids
   */
  fetchElementsWatchlistMapping(elementUuids: string[]): Observable<ElementWatchlistMapping> {
    const url = this._buildUrl(`${ environment.api_url }watchlists/checkContains`, true);
    return this._httpClient.post(url, { objectUuids: elementUuids }).pipe(
      map(response => response ? response as ElementWatchlistMapping : null)
    );
  }

  private _createObjectFromFlatlist(watchlists: WatchlistBackendInterface[]): {[uuid: string]: Watchlist} {
    return watchlists.reduce((flattWatchlists, watchlist) => {
      flattWatchlists[watchlist.Uuid] = generateWatchlistFromBackendInput(watchlist);
      return flattWatchlists;
    }, {});
  }

  private _buildUrl(url: string, firstQueryParam = false): string {
    if (this._treeUuid) {
      const urlOperator = firstQueryParam ? '?' : '&';
      url += urlOperator + `treeUuid=${ this._treeUuid }`;
      firstQueryParam = false;
    }

    if (environment.catalogIdentifier) {
      const urlOperator = firstQueryParam ? '?' : '&';
      url += urlOperator + `catalogIdentifier=${ environment.catalogIdentifier }`;
    }
    return url;
  }
}
